/* Ventrin onboarding — 7-scene guided setup.
 *
 * Design principles (from the brief):
 *   - Fast by default: main path under 50 seconds.
 *   - Smart defaults, progressive disclosure.
 *   - Feels like a premium product flow, not a settings wizard.
 *   - Every choice maps into real backend state (workspace, domains,
 *     invites, settings) — no pretty-but-fake UI.
 *
 * Implementation:
 *   - Single React component, scene state machine.
 *   - CSS-only transitions (fade + slight translate). No JS motion libs.
 *   - Ambient SVG grid + per-scene hero glyph.
 *   - Extension detection via window.postMessage handshake with a 1500ms
 *     timeout. The content script (in the extension) echoes a pong back
 *     when it sees the ping on app.ventrin.com.
 *   - Skip-orientation jump to a safe defaults + install scene.
 *   - Surprise flourish on completion: radial node burst (no 3rd-party).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth-context";
import { Logo } from "../components/Logo";

// ────────────────────────────────────────────────────────────────────────────
// Types

type Scene =
  | "welcome"
  | "workspace"
  | "preset"
  | "tools"
  | "behaviour"
  | "invites"
  | "install"
  | "complete";

type Vertical = "legal" | "healthcare" | "generic";

type ToolMode = "protect" | "monitor" | "block";
type ToolKey = "chatgpt" | "claude" | "gemini" | "copilot";

type Behaviour = "clean_and_warn" | "warn_only" | "block_high_risk";

type DataTypeKey = "client" | "internal" | "financial" | "credentials";

type ExtStatus =
  | "unknown"
  | "probing"
  | "installed_connected"
  | "installed_disconnected"
  | "not_installed";

interface OnboardingState {
  scene: Scene;
  skippedOrientation: boolean;
  workspaceId?: string;

  // scene 2
  workspaceName: string;

  // scene 3
  vertical: Vertical;
  dataTypes: Record<DataTypeKey, boolean>;

  // scene 4
  toolModes: Record<ToolKey, ToolMode>;

  // scene 5
  behaviour: Behaviour;

  // scene 6
  inviteEmails: string[];

  // scene 7
  extStatus: ExtStatus;
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers

const SMART_DEFAULTS: Pick<OnboardingState,
  "vertical" | "dataTypes" | "toolModes" | "behaviour"> = {
  vertical: "generic",
  dataTypes: { client: false, internal: true, financial: false, credentials: true },
  toolModes: { chatgpt: "protect", claude: "protect", gemini: "protect", copilot: "protect" },
  behaviour: "clean_and_warn",
};

function defaultsForVertical(v: Vertical): Pick<OnboardingState, "dataTypes" | "behaviour"> {
  switch (v) {
    case "legal":
      return {
        dataTypes: { client: true, internal: true, financial: false, credentials: true },
        behaviour: "clean_and_warn",
      };
    case "healthcare":
      return {
        dataTypes: { client: true, internal: true, financial: true, credentials: true },
        behaviour: "block_high_risk",
      };
    default:
      return {
        dataTypes: { client: false, internal: true, financial: false, credentials: true },
        behaviour: "clean_and_warn",
      };
  }
}

function suggestWorkspaceName(email: string | null | undefined): string {
  if (!email) return "";
  const domain = email.split("@")[1] ?? "";
  if (!domain) return "";
  const core = domain.split(".")[0];
  if (!core || core.length < 2) return "";
  // Common domains we don't suggest — people want a real org name there.
  if (["gmail", "googlemail", "hotmail", "yahoo", "outlook", "icloud", "proton"].includes(core.toLowerCase())) {
    return "";
  }
  return core.charAt(0).toUpperCase() + core.slice(1);
}

const SCENE_ORDER: Scene[] = [
  "welcome", "workspace", "preset", "tools", "behaviour", "invites", "install", "complete",
];

function sceneIndex(s: Scene): number { return SCENE_ORDER.indexOf(s); }
function nextScene(s: Scene): Scene { return SCENE_ORDER[sceneIndex(s) + 1] ?? "complete"; }
function prevScene(s: Scene): Scene { return SCENE_ORDER[Math.max(0, sceneIndex(s) - 1)]; }

// Extension handshake via window.postMessage. The content script (running in
// extension/src/content.ts) listens on app.ventrin.com and echoes a pong.
function probeExtension(timeoutMs = 1500): Promise<ExtStatus> {
  return new Promise((resolve) => {
    const nonce = Math.random().toString(36).slice(2);
    let done = false;
    const onMessage = (e: MessageEvent) => {
      const d = e.data;
      if (!d || typeof d !== "object") return;
      if (d.__ventrin_pong === true && d.nonce === nonce) {
        done = true;
        window.removeEventListener("message", onMessage);
        resolve(d.connected === false ? "installed_disconnected" : "installed_connected");
      }
    };
    window.addEventListener("message", onMessage);
    window.postMessage({ __ventrin_ping: true, nonce }, "*");
    setTimeout(() => {
      if (done) return;
      window.removeEventListener("message", onMessage);
      resolve("not_installed");
    }, timeoutMs);
  });
}

// ────────────────────────────────────────────────────────────────────────────
// Root component

export function OnboardingPage() {
  const { user, refresh, isDemo } = useAuth();
  const navigate = useNavigate();
  const userEmail = user?.email ?? null;

  // Demo users walk through the tour without mutating the shared workspace.
  // Every API call below becomes a no-op when isDemo is true. A sessionStorage
  // flag lets the Gate bounce them straight to /dashboard after they're done.
  const markDemoTourDone = () => {
    if (isDemo) sessionStorage.setItem("vt_demo_onboarding_seen", "1");
  };

  // Demo users land with a pre-filled workspace name (they're touring the
  // populated demo workspace, not making a real one).
  const initialName = isDemo ? "Demo workspace" : suggestWorkspaceName(userEmail);

  const [state, setState] = useState<OnboardingState>(() => ({
    scene: "welcome",
    skippedOrientation: false,
    workspaceName: initialName,
    ...SMART_DEFAULTS,
    inviteEmails: [],
    extStatus: "unknown",
  }));

  const [transitioning, setTransitioning] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [verificationNote, setVerificationNote] = useState<string | null>(null);

  function patch(p: Partial<OnboardingState>) {
    setState((s) => ({ ...s, ...p }));
  }

  function goTo(target: Scene) {
    if (target === state.scene) return;
    setTransitioning(true);
    setTimeout(() => {
      setState((s) => ({ ...s, scene: target }));
      setTimeout(() => setTransitioning(false), 60);
    }, 160);
  }

  // ── Actions ─────────────────────────────────────────────────────────────

  async function createWorkspaceIfNeeded(): Promise<string | undefined> {
    if (state.workspaceId) return state.workspaceId;
    if (isDemo) return "demo"; // tour mode — never mutates the shared demo tenant
    if (!state.workspaceName.trim()) return undefined;
    const { workspaceId } = await api.createWorkspace({
      name: state.workspaceName.trim(),
      verticalType: state.vertical,
    });
    patch({ workspaceId });
    return workspaceId;
  }

  async function advanceFromWorkspace() {
    setErr(null);
    setVerificationNote(null);
    try {
      await createWorkspaceIfNeeded();
      goTo("preset");
    } catch (ex: any) {
      const msg = ex?.message ?? "Could not create workspace.";
      if (/verify your email/i.test(msg)) {
        setErr("Please verify your email first. Check your inbox for a link from Ventrin.");
      } else {
        setErr(msg);
      }
    }
  }

  async function resendVerification() {
    if (!user) return;
    setVerificationNote(null);
    try {
      // Lazy-import so we don't pull firebase/auth into the onboarding
      // bundle until the user actually asks to resend.
      const { sendEmailVerification } = await import("firebase/auth");
      await sendEmailVerification(user);
      setVerificationNote("Verification email sent. Check your inbox, click the link, then press Continue.");
    } catch (ex: any) {
      setVerificationNote(ex?.message ?? "Could not send verification email.");
    }
  }

  async function iHaveVerified() {
    if (!user) return;
    setVerificationNote("Checking…");
    try {
      await user.reload();
      if ((user as any).emailVerified) {
        setErr(null);
        setVerificationNote(null);
        advanceFromWorkspace();
      } else {
        setVerificationNote("Still not verified. Check your inbox, click the link, then press this button again.");
      }
    } catch (ex: any) {
      setVerificationNote(ex?.message ?? "Could not check verification status.");
    }
  }

  async function advanceFromPreset() {
    setErr(null);
    if (!isDemo) {
      try {
        // Map the onboarding vertical + data-type toggles to policy-pack
        // ids. Mirrors packsForProfile() in shared/src/packs.ts — kept
        // inline because Vite's workspace resolver doesn't always surface
        // runtime named exports from @ventrin/shared.
        const activePackIds = (() => {
          const ids = ["general"];
          if (state.vertical === "legal") ids.push("legal");
          if (state.vertical === "healthcare") ids.push("healthcare");
          if (state.dataTypes?.client && !ids.includes("legal")) ids.push("legal");
          return ids;
        })();
        await api.updateWorkspaceSettings({
          activePackIds,
          settings: {
            industryContextVertical: state.vertical,
            selectedDataTypes: state.dataTypes,
          },
        });
      } catch (ex: any) {
        setErr(ex?.message ?? "Could not save your preset.");
        return;
      }
    }
    goTo("tools");
  }

  async function advanceFromTools() {
    setErr(null);
    if (!isDemo) {
      try {
        const domainUpdates = Object.entries(state.toolModes).map(([tool, mode]) => {
          const defaultActionMode =
            mode === "block" ? "blocked" :
            mode === "monitor" ? "allowed_warn" : "allowed";
          return api.updateDomainConfig({
            domainKey: tool,
            enabled: mode !== "block",
            defaultActionMode,
          });
        });
        await Promise.all(domainUpdates);
      } catch (ex: any) {
        console.warn("[onboarding] tool update partial failure", ex);
      }
    }
    goTo("behaviour");
  }

  async function advanceFromBehaviour() {
    setErr(null);
    if (!isDemo) {
      try {
        const anonymisationMode: "auto" | "suggest" | "block_if_unsafe" =
          state.behaviour === "clean_and_warn" ? "suggest" :
          state.behaviour === "block_high_risk" ? "block_if_unsafe" :
          "suggest";
        await api.updateWorkspaceSettings({
          settings: {
            anonymisationMode,
            allowUserContinueOverride: state.behaviour !== "block_high_risk",
          },
        });
      } catch (ex: any) {
        console.warn("[onboarding] behaviour save failed", ex);
      }
    }
    goTo("invites");
  }

  async function advanceFromInvites() {
    setErr(null);
    if (!isDemo && state.inviteEmails.length) {
      try {
        await Promise.all(state.inviteEmails.map((email) =>
          api.createInvite({ email, usageLimit: 1, expiresInDays: 7 })
        ));
      } catch (ex: any) {
        setErr(ex?.message ?? "Could not send invites.");
        return;
      }
    }
    goTo("install");
    patch({ extStatus: "probing" });
    probeExtension().then((status) => patch({ extStatus: status }));
  }

  async function applySkipDefaults() {
    setErr(null);
    patch({ ...SMART_DEFAULTS, skippedOrientation: true });
    if (!isDemo) {
      try {
        if (!state.workspaceId) {
          const name = state.workspaceName.trim() || suggestWorkspaceName(userEmail) || "My workspace";
          const { workspaceId } = await api.createWorkspace({ name, verticalType: "generic" });
          patch({ workspaceId });
        }
      } catch (ex: any) {
        setErr(ex?.message ?? "Could not create workspace.");
        return;
      }
    }
    goTo("install");
    patch({ extStatus: "probing" });
    probeExtension().then((status) => patch({ extStatus: status }));
  }

  /** One-shot "exit now" for demo users — skips the whole tour and lands in
   *  the populated demo workspace. Non-demo users get the standard finish
   *  behaviour (which goes through the welcome modal). */
  function skipTour() {
    markDemoTourDone();
    navigate("/");
  }

  async function finishOnboarding() {
    markDemoTourDone();
    if (!isDemo) await refresh();
  }

  // ── Render ───────────────────────────────────────────────────────────────

  const stepsTotal = 6;  // welcome + workspace + preset + tools + behaviour + invites + install = shown as 6 progress dots
  const stepIndex = Math.min(stepsTotal - 1, Math.max(0, sceneIndex(state.scene) - 1));

  return (
    <div className={`vo-shell ${transitioning ? "is-transitioning" : ""}`}>
      <AmbientBackground scene={state.scene} />

      {state.scene !== "welcome" && state.scene !== "complete" && (
        <ProgressBar current={stepIndex} total={stepsTotal} />
      )}

      <header className="vo-header">
        <div className="vo-brand">
          <Logo size={20} />
          <span>Ventrin</span>
          {isDemo && <span className="vo-demo-badge">Demo tour</span>}
        </div>
        <div className="vo-header-right">
          {isDemo && state.scene !== "complete" && (
            <button className="vo-link vo-link-skip" type="button" onClick={skipTour}>
              Skip tour &rarr;
            </button>
          )}
        </div>
      </header>

      <main className="vo-main">
        <div className={`vo-scene vo-scene-${state.scene}`}>
          {state.scene === "welcome" && (
            <WelcomeScene
              onStart={() => goTo("workspace")}
              onSkip={() => applySkipDefaults()}
            />
          )}
          {state.scene === "workspace" && (
            <WorkspaceScene
              value={state.workspaceName}
              onChange={(v) => patch({ workspaceName: v })}
              onBack={() => goTo(prevScene(state.scene))}
              onNext={advanceFromWorkspace}
              err={err}
              needsVerification={!!err && /verify your email/i.test(err)}
              onResend={resendVerification}
              onIHaveVerified={iHaveVerified}
              verificationNote={verificationNote}
            />
          )}
          {state.scene === "preset" && (
            <PresetScene
              vertical={state.vertical}
              dataTypes={state.dataTypes}
              onVerticalChange={(v) => {
                const d = defaultsForVertical(v);
                patch({ vertical: v, ...d });
              }}
              onDataTypeToggle={(k) =>
                patch({ dataTypes: { ...state.dataTypes, [k]: !state.dataTypes[k] } })
              }
              onBack={() => goTo(prevScene(state.scene))}
              onNext={advanceFromPreset}
              workspaceName={state.workspaceName}
            />
          )}
          {state.scene === "tools" && (
            <ToolsScene
              toolModes={state.toolModes}
              onModeChange={(t, m) => patch({ toolModes: { ...state.toolModes, [t]: m } })}
              onBack={() => goTo(prevScene(state.scene))}
              onNext={advanceFromTools}
            />
          )}
          {state.scene === "behaviour" && (
            <BehaviourScene
              value={state.behaviour}
              onChange={(b) => patch({ behaviour: b })}
              onBack={() => goTo(prevScene(state.scene))}
              onNext={advanceFromBehaviour}
            />
          )}
          {state.scene === "invites" && (
            <InvitesScene
              emails={state.inviteEmails}
              onChange={(emails) => patch({ inviteEmails: emails })}
              onBack={() => goTo(prevScene(state.scene))}
              onNext={advanceFromInvites}
              err={err}
            />
          )}
          {state.scene === "install" && (
            <InstallScene
              status={state.extStatus}
              onRetry={() => {
                patch({ extStatus: "probing" });
                probeExtension().then((status) => patch({ extStatus: status }));
              }}
              onNext={() => goTo("complete")}
              onLater={() => goTo("complete")}
            />
          )}
          {state.scene === "complete" && (
            <CompleteScene
              state={state}
              onOpenDashboard={async () => { await finishOnboarding(); navigate("/"); }}
            />
          )}
        </div>
      </main>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Ambient background — faint grid pulse (SVG + CSS anim)

function AmbientBackground({ scene }: { scene: Scene }) {
  return (
    <div className="vo-ambient" aria-hidden>
      <svg className="vo-ambient-grid" viewBox="0 0 1200 800" preserveAspectRatio="none">
        <defs>
          <pattern id="vo-grid" width="56" height="56" patternUnits="userSpaceOnUse">
            <path d="M 56 0 L 0 0 0 56" fill="none" stroke="currentColor" strokeWidth="0.6" />
          </pattern>
          <radialGradient id="vo-fade" cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="black" stopOpacity="0.9" />
            <stop offset="100%" stopColor="black" stopOpacity="0" />
          </radialGradient>
          <mask id="vo-mask">
            <rect width="100%" height="100%" fill="url(#vo-fade)" />
          </mask>
        </defs>
        <g mask="url(#vo-mask)">
          <rect width="100%" height="100%" fill="url(#vo-grid)" />
        </g>
      </svg>
      <div className={`vo-ambient-signal vo-ambient-signal-${scene}`} />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Progress bar — segmented thin line pinned to top

function ProgressBar({ current, total }: { current: number; total: number }) {
  return (
    <div className="vo-progress" aria-label={`Step ${current + 1} of ${total}`}>
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          className={`vo-progress-seg ${i <= current ? "is-filled" : ""} ${i === current ? "is-current" : ""}`}
        />
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Scenes

function WelcomeScene({ onStart, onSkip }: { onStart: () => void; onSkip: () => void }) {
  return (
    <div className="vo-welcome">
      <div className="vo-welcome-art" aria-hidden>
        <svg viewBox="0 0 220 220">
          <defs>
            <radialGradient id="vo-w-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#4870a8" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#4870a8" stopOpacity="0" />
            </radialGradient>
          </defs>
          <circle cx="110" cy="110" r="110" fill="url(#vo-w-glow)" />
          <g className="vo-w-ring"
             fill="none" stroke="#2c4a6a" strokeWidth="1.4" opacity="0.55">
            <circle cx="110" cy="110" r="80" />
            <circle cx="110" cy="110" r="56" />
          </g>
          <g className="vo-w-nodes" fill="#2c4a6a">
            <circle cx="110" cy="30" r="3" />
            <circle cx="190" cy="110" r="3" />
            <circle cx="110" cy="190" r="3" />
            <circle cx="30" cy="110" r="3" />
            <circle cx="168" cy="52" r="2.2" />
            <circle cx="168" cy="168" r="2.2" />
            <circle cx="52" cy="52" r="2.2" />
            <circle cx="52" cy="168" r="2.2" />
          </g>
          <g className="vo-w-core" fill="#1a1d21">
            <path d="M105 95 L115 95 L115 125 L105 125 Z" />
            <path d="M95 105 L125 105 L125 115 L95 115 Z" />
          </g>
        </svg>
      </div>
      <h1 className="vo-h1">Set up Ventrin in under a minute.</h1>
      <p className="vo-sub">Choose a few basics and start protecting your team across AI tools.</p>
      <div className="vo-actions">
        <button type="button" className="vo-btn vo-btn-primary vo-btn-lg" onClick={onStart}>
          Get started
        </button>
        <button type="button" className="vo-btn vo-btn-ghost vo-btn-lg" onClick={onSkip}>
          Skip orientation
        </button>
      </div>
      <p className="vo-foot-hint">You can change everything later.</p>
    </div>
  );
}

function WorkspaceScene({
  value, onChange, onBack, onNext, err,
  needsVerification, onResend, onIHaveVerified, verificationNote,
}: {
  value: string;
  onChange: (v: string) => void;
  onBack: () => void;
  onNext: () => void;
  err: string | null;
  needsVerification: boolean;
  onResend: () => void;
  onIHaveVerified: () => void;
  verificationNote: string | null;
}) {
  const ref = useRef<HTMLInputElement | null>(null);
  useEffect(() => { ref.current?.focus(); }, []);
  const disabled = !value.trim();

  return (
    <div className="vo-form-scene">
      <SceneTitle eyebrow="Step 1 of 6" title="Name your workspace." sub="This is how your organisation will appear inside Ventrin." />
      <div className="vo-field vo-field-wide">
        <input
          ref={ref}
          className="vo-input"
          placeholder="e.g. Acme Legal"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !disabled) onNext(); }}
          aria-label="Workspace name"
        />
      </div>
      {err && <div className="vo-err">{err}</div>}
      {needsVerification && (
        <div className="vo-verify-block">
          <div className="vo-verify-title">Verify your email</div>
          <div className="vo-verify-body">
            We sent a verification link when you signed up. Open the email and click the link, then come back here.
          </div>
          <div className="vo-verify-actions">
            <button type="button" className="vo-btn vo-btn-secondary" onClick={onIHaveVerified}>I've verified</button>
            <button type="button" className="vo-btn vo-btn-ghost" onClick={onResend}>Resend email</button>
          </div>
          {verificationNote && <div className="vo-verify-note">{verificationNote}</div>}
        </div>
      )}
      <SceneNav onBack={onBack} onNext={onNext} disabled={disabled} />
    </div>
  );
}

function PresetScene({
  vertical, dataTypes, onVerticalChange, onDataTypeToggle, onBack, onNext, workspaceName,
}: {
  vertical: Vertical;
  dataTypes: Record<DataTypeKey, boolean>;
  onVerticalChange: (v: Vertical) => void;
  onDataTypeToggle: (k: DataTypeKey) => void;
  onBack: () => void;
  onNext: () => void;
  workspaceName: string;
}) {
  const verticals: Array<{ key: Vertical; label: string; glyph: JSX.Element }> = [
    { key: "legal",      label: "Legal",      glyph: <IconScales /> },
    { key: "healthcare", label: "Healthcare", glyph: <IconCross /> },
    { key: "generic",    label: "General",    glyph: <IconShield /> },
  ];
  const dataTypesList: Array<{ key: DataTypeKey; label: string }> = [
    { key: "client",      label: "Client or patient data" },
    { key: "internal",    label: "Internal company data" },
    { key: "financial",   label: "Financial information" },
    { key: "credentials", label: "Credentials and secrets" },
  ];
  return (
    <div className="vo-form-scene">
      <SceneTitle
        eyebrow="Step 2 of 6"
        title={workspaceName ? `Ventrin is almost ready for ${workspaceName}.` : "What do you want to protect?"}
        sub="Pick your industry and the kinds of data to guard. We'll apply sensible defaults."
      />
      <div className="vo-preset-grid">
        {verticals.map((v) => (
          <button
            key={v.key}
            type="button"
            className={`vo-preset-card ${vertical === v.key ? "is-selected" : ""}`}
            onClick={() => onVerticalChange(v.key)}
          >
            <span className="vo-preset-icon" aria-hidden>{v.glyph}</span>
            <span className="vo-preset-label">{v.label}</span>
          </button>
        ))}
      </div>
      <div className="vo-datatypes">
        <div className="vo-section-label">Also protect:</div>
        <div className="vo-datatypes-list">
          {dataTypesList.map((d) => (
            <label key={d.key} className={`vo-checkrow ${dataTypes[d.key] ? "is-on" : ""}`}>
              <input
                type="checkbox"
                checked={!!dataTypes[d.key]}
                onChange={() => onDataTypeToggle(d.key)}
              />
              <span className="vo-checkrow-box" aria-hidden />
              <span>{d.label}</span>
            </label>
          ))}
        </div>
      </div>
      <SceneNav onBack={onBack} onNext={onNext} />
    </div>
  );
}

function ToolsScene({
  toolModes, onModeChange, onBack, onNext,
}: {
  toolModes: Record<ToolKey, ToolMode>;
  onModeChange: (t: ToolKey, m: ToolMode) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const tools: Array<{ key: ToolKey; label: string }> = [
    { key: "chatgpt", label: "ChatGPT" },
    { key: "claude",  label: "Claude" },
    { key: "gemini",  label: "Gemini" },
    { key: "copilot", label: "Microsoft Copilot" },
  ];
  return (
    <div className="vo-form-scene">
      <SceneTitle
        eyebrow="Step 3 of 6"
        title="Choose the tools your team uses."
        sub={<>Protect means Ventrin checks prompts and removes sensitive data before anything is sent.</>}
      />
      <div className="vo-tools">
        {tools.map((t) => (
          <div key={t.key} className="vo-tool-row">
            <div className="vo-tool-name">{t.label}</div>
            <div className="vo-seg" role="radiogroup" aria-label={`${t.label} mode`}>
              {(["protect", "monitor", "block"] as ToolMode[]).map((m) => (
                <button
                  key={m}
                  type="button"
                  role="radio"
                  aria-checked={toolModes[t.key] === m}
                  className={`vo-seg-btn ${toolModes[t.key] === m ? "is-active" : ""} vo-seg-${m}`}
                  onClick={() => onModeChange(t.key, m)}
                >
                  {m === "protect" ? "Protect" : m === "monitor" ? "Monitor" : "Block"}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <SceneNav onBack={onBack} onNext={onNext} />
    </div>
  );
}

function BehaviourScene({
  value, onChange, onBack, onNext,
}: {
  value: Behaviour;
  onChange: (b: Behaviour) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const options: Array<{ key: Behaviour; label: string; desc: string; recommended?: boolean }> = [
    { key: "clean_and_warn", label: "Clean and warn", desc: "Sanitise sensitive content and show a safer version for review.", recommended: true },
    { key: "warn_only", label: "Warn before sending", desc: "Flag risky prompts but let the user decide each time." },
    { key: "block_high_risk", label: "Block high-risk prompts", desc: "Block anything that looks high risk outright — the strictest option." },
  ];
  return (
    <div className="vo-form-scene">
      <SceneTitle eyebrow="Step 4 of 6" title="How should Ventrin respond?" sub="Pick the default reaction. You can tune this later for specific teams." />
      <div className="vo-behaviour">
        {options.map((o) => (
          <button
            key={o.key}
            type="button"
            className={`vo-behaviour-card ${value === o.key ? "is-selected" : ""}`}
            onClick={() => onChange(o.key)}
          >
            <div className="vo-behaviour-head">
              <span className="vo-behaviour-title">{o.label}</span>
              {o.recommended && <span className="vo-pill">Recommended</span>}
            </div>
            <div className="vo-behaviour-desc">{o.desc}</div>
          </button>
        ))}
      </div>
      <SceneNav onBack={onBack} onNext={onNext} />
    </div>
  );
}

function InvitesScene({
  emails, onChange, onBack, onNext, err,
}: {
  emails: string[];
  onChange: (emails: string[]) => void;
  onBack: () => void;
  onNext: () => void;
  err: string | null;
}) {
  const [input, setInput] = useState("");

  function tryAdd(raw: string) {
    const parts = raw.split(/[\s,;]+/).map((p) => p.trim()).filter(Boolean);
    const next = [...emails];
    for (const p of parts) {
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p) && !next.includes(p)) next.push(p);
    }
    onChange(next);
  }

  return (
    <div className="vo-form-scene">
      <SceneTitle eyebrow="Step 5 of 6" title="Invite your team." sub="Invite now or do it later from your workspace." />
      <div className="vo-invite-add">
        <input
          className="vo-input"
          placeholder="teammate@company.com"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              tryAdd(input);
              setInput("");
            }
          }}
          onBlur={() => { if (input) { tryAdd(input); setInput(""); } }}
          onPaste={(e) => {
            const text = e.clipboardData.getData("text");
            if (text && /[,;\s]/.test(text)) {
              e.preventDefault();
              tryAdd(text);
              setInput("");
            }
          }}
          aria-label="Invite email"
        />
        <button
          type="button"
          className="vo-btn vo-btn-secondary"
          onClick={() => { if (input) { tryAdd(input); setInput(""); } }}
        >
          Add
        </button>
      </div>
      {emails.length > 0 && (
        <ul className="vo-invite-list">
          {emails.map((e) => (
            <li key={e}>
              <span>{e}</span>
              <button
                type="button"
                className="vo-invite-remove"
                aria-label={`Remove ${e}`}
                onClick={() => onChange(emails.filter((x) => x !== e))}
              >&times;</button>
            </li>
          ))}
        </ul>
      )}
      {err && <div className="vo-err">{err}</div>}
      <SceneNav
        onBack={onBack}
        onNext={onNext}
        nextLabel={emails.length ? "Send invites and continue" : "Skip for now"}
        nextVariant={emails.length ? "primary" : "ghost"}
      />
    </div>
  );
}

function InstallScene({
  status, onRetry, onNext, onLater,
}: {
  status: ExtStatus;
  onRetry: () => void;
  onNext: () => void;
  onLater: () => void;
}) {
  // When the extension is already connected we skip the whole install
  // panel (no "download again" button, no "I've connected it" retry).
  // Just a clean confirmation + a single Continue button.
  if (status === "installed_connected") {
    return (
      <div className="vo-form-scene vo-install-scene">
        <SceneTitle
          eyebrow="Step 6 of 6"
          title="Ventrin is active in this browser."
          sub="You're all set. Prompts on ChatGPT, Claude, Gemini and Copilot will be checked before anything leaves your browser."
        />
        <div className="vo-install-confirmed" aria-live="polite">
          <span className="vo-install-confirmed-mark" aria-hidden>
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12l5 5L20 7" />
            </svg>
          </span>
          <span>Extension connected</span>
        </div>
        <div className="vo-actions">
          <button className="vo-btn vo-btn-primary vo-btn-lg" onClick={onNext}>Finish Setup</button>
        </div>
      </div>
    );
  }

  const copy = (() => {
    switch (status) {
      case "installed_disconnected":
        return {
          headline: "Your extension is installed.",
          sub: "Finish connecting it to this workspace. Reload this tab after signing in inside the extension.",
          primary: "I've connected it",
          primaryAction: "retry" as const,
        };
      case "probing":
        return {
          headline: "Checking for Ventrin…",
          sub: "Looking for a connected extension in this browser.",
          primary: "Checking…",
          primaryAction: "none" as const,
        };
      case "not_installed":
      case "unknown":
      default:
        return {
          headline: "Activate Ventrin protection.",
          sub: "Install the Ventrin browser extension. Protection starts the moment it's connected to this workspace.",
          primary: "Download for Chrome",
          primaryAction: "download" as const,
        };
    }
  })();

  return (
    <div className="vo-form-scene vo-install-scene">
      <SceneTitle eyebrow="Step 6 of 6" title={copy.headline} sub={copy.sub} />

      <div className="vo-install-panel">
        <div className="vo-install-logo" aria-hidden>
          <Logo size={28} />
        </div>
        <div className="vo-install-panel-body">
          <div className="vo-install-panel-title">Ventrin for Chrome</div>
          <div className={`vo-install-badge vo-install-${status}`} aria-live="polite">
            <span className="vo-install-dot" />
            <span className="vo-install-text">
              {status === "installed_disconnected" && "Installed, not connected"}
              {status === "probing" && "Checking browser…"}
              {(status === "not_installed" || status === "unknown") && "Not detected"}
            </span>
          </div>
        </div>
      </div>

      <div className="vo-actions">
        {copy.primaryAction === "download" && (
          <a
            className="vo-btn vo-btn-primary vo-btn-lg"
            href="https://www.ventrin.com/download.html"
            target="_blank" rel="noopener"
            onClick={() => setTimeout(onRetry, 3000)}
          >
            {copy.primary}
          </a>
        )}
        {copy.primaryAction === "retry" && (
          <button className="vo-btn vo-btn-primary vo-btn-lg" onClick={onRetry}>{copy.primary}</button>
        )}
        {copy.primaryAction === "none" && (
          <button className="vo-btn vo-btn-primary vo-btn-lg" disabled>{copy.primary}</button>
        )}
        {status !== "probing" && (
          <button className="vo-btn vo-btn-ghost vo-btn-lg" onClick={onLater}>
            I'll do this later
          </button>
        )}
      </div>

      <p className="vo-foot-hint">You can come back to this step any time from Settings.</p>
    </div>
  );
}

function CompleteScene({
  state, onOpenDashboard,
}: {
  state: OnboardingState;
  onOpenDashboard: () => void;
}) {
  const toolDisplay: Record<ToolKey, string> = {
    chatgpt: "ChatGPT", claude: "Claude", gemini: "Gemini", copilot: "Copilot",
  };
  const enabledTools = Object.entries(state.toolModes)
    .filter(([, m]) => m !== "block")
    .map(([t]) => toolDisplay[t as ToolKey] ?? t)
    .join(", ");
  const extLabel =
    state.extStatus === "installed_connected" ? "Connected" :
    state.extStatus === "installed_disconnected" ? "Installed" :
    state.extStatus === "probing" ? "Checking…" : "Not Yet Installed";

  return (
    <div className="vo-complete">
      <div className="vo-burst" aria-hidden>
        {Array.from({ length: 12 }).map((_, i) => (
          <span key={i} className="vo-burst-node" style={{ "--i": i } as any} />
        ))}
      </div>
      <h1 className="vo-h1">Your workspace is ready.</h1>
      <p className="vo-sub">A clean starting point. Tune anything later from Settings.</p>

      <div className="vo-summary">
        <SummaryRow label="Workspace" value={state.workspaceName || "—"} />
        <SummaryRow label="Preset" value={titleCase(state.vertical)} />
        <SummaryRow label="Tools" value={enabledTools || "None"} />
        <SummaryRow
          label="Invites"
          value={state.inviteEmails.length ? `${state.inviteEmails.length} sent` : "Skipped"}
        />
        <SummaryRow label="Extension" value={extLabel} />
      </div>

      <div className="vo-actions">
        <button className="vo-btn vo-btn-primary vo-btn-lg" onClick={onOpenDashboard}>Go To Dashboard</button>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="vo-summary-row">
      <span className="vo-summary-label">{label}</span>
      <span className="vo-summary-value">{value}</span>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Small shared UI

function SceneTitle({
  eyebrow, title, sub,
}: {
  eyebrow?: string;
  title: string | JSX.Element;
  sub: string | JSX.Element;
}) {
  return (
    <header className="vo-scene-head">
      {eyebrow && <div className="vo-eyebrow">{eyebrow}</div>}
      <h1 className="vo-h2">{title}</h1>
      <p className="vo-sub">{sub}</p>
    </header>
  );
}

function SceneNav({
  onBack, onNext, disabled, nextLabel, nextVariant,
}: {
  onBack: () => void;
  onNext: () => void;
  disabled?: boolean;
  nextLabel?: string;
  nextVariant?: "primary" | "ghost";
}) {
  const variant = nextVariant ?? "primary";
  return (
    <div className="vo-nav">
      <button type="button" className="vo-btn vo-btn-ghost" onClick={onBack}>Back</button>
      <button
        type="button"
        className={`vo-btn ${variant === "primary" ? "vo-btn-primary" : "vo-btn-secondary"}`}
        onClick={onNext}
        disabled={disabled}
      >
        {nextLabel ?? "Continue"}
      </button>
    </div>
  );
}

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ────────────────────────────────────────────────────────────────────────────
// Preset icons — single-path SVGs, currentColor

function IconScales() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v18M4 21h16M6 8l-3 7a3 3 0 0 0 6 0l-3-7zM18 8l-3 7a3 3 0 0 0 6 0l-3-7zM6 8h12" />
    </svg>
  );
}
function IconCross() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M12 8v8M8 12h8" />
    </svg>
  );
}
function IconCoin() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="8.5" />
      <path d="M9 10h5a2 2 0 1 1 0 4H9M12 8v8" />
    </svg>
  );
}
function IconShield() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2.5l8 3v5.5c0 4.6-3.2 8.8-8 9.5-4.8-.7-8-4.9-8-9.5V5.5l8-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}
